import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  FileText, 
  Bell, 
  Calendar,
  BarChart3,
  Settings,
  Home,
  Lightbulb,
  Users,
  Clock
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import NewsTicker from './NewsTicker';

const TenantPortal = () => {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [tenantId, setTenantId] = useState(null);

  // Magic Link Login Logic
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      try {
        // Simple JWT decode (payload is base64 encoded in the second part)
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const payload = JSON.parse(jsonPayload);
        
        if (payload.tenant_id) {
          setTenantId(payload.tenant_id);
          console.log('[PORTAL] Logged in via Magic Link for tenant:', payload.tenant_id);
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch (e) {
        console.error('Invalid magic link token', e);
      }
    }
  }, []);

  // Get theme-specific classes
  const getThemeClasses = () => {
    switch (theme) {
      case 'light':
        return {
          background: 'bg-gray-50',
          cardBackground: 'bg-white',
          text: 'text-gray-900',
          textSecondary: 'text-gray-600',
          border: 'border-gray-200',
          hover: 'hover:bg-gray-100',
          accent: 'bg-blue-600',
          accentHover: 'hover:bg-blue-700'
        };
      case 'dark':
        return {
          background: 'bg-gray-900',
          cardBackground: 'bg-gray-800',
          text: 'text-gray-100',
          textSecondary: 'text-gray-300',
          border: 'border-gray-700',
          hover: 'hover:bg-gray-700',
          accent: 'bg-blue-600',
          accentHover: 'hover:bg-blue-700'
        };
      default:
        return {
          background: 'bg-gray-50',
          cardBackground: 'bg-white',
          text: 'text-gray-900',
          textSecondary: 'text-gray-600',
          border: 'border-gray-200',
          hover: 'hover:bg-gray-100',
          accent: 'bg-blue-600',
          accentHover: 'hover:bg-blue-700'
        };
    }
  };

  const themeClasses = getThemeClasses();

  // Dashboard Cards Data
  const dashboardCards = [
    {
      id: 'messages',
      title: 'Nachrichten',
      description: 'Direktkommunikation mit der Verwaltung',
      icon: MessageSquare,
      count: '3 neue',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      action: () => setActiveTab('messages')
    },
    {
      id: 'documents',
      title: 'Dokumente',
      description: 'Mietvertrag, Nebenkostenabrechnungen',
      icon: FileText,
      count: '5 Dokumente',
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      action: () => setActiveTab('documents')
    },
    {
      id: 'announcements',
      title: 'Schwarzes Brett',
      description: 'Ankündigungen und wichtige Informationen',
      icon: Bell,
      count: '2 neue',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
      action: () => setActiveTab('announcements')
    },
    {
      id: 'maintenance',
      title: 'Schadensmeldung',
      description: 'Defekte und Reparaturen melden',
      icon: Settings,
      count: '1 offen',
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      action: () => setActiveTab('maintenance')
    },
    {
      id: 'consumption',
      title: 'Verbrauchsanalyse',
      description: 'Strom, Wasser, Heizung im Überblick',
      icon: BarChart3,
      count: 'Aktuell',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      action: () => setActiveTab('consumption')
    },
    {
      id: 'appointments',
      title: 'Termine',
      description: 'Besichtigungen und Reparaturtermine',
      icon: Calendar,
      count: '1 anstehend',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100',
      action: () => setActiveTab('appointments')
    }
  ];

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className={`${themeClasses.cardBackground} rounded-lg border ${themeClasses.border} p-6`}>
        <div className="flex items-center space-x-3 mb-4">
          <div className={`p-3 rounded-lg ${themeClasses.accent}`}>
            <Lightbulb className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className={`text-2xl font-bold ${themeClasses.text}`}>
              Willkommen im Mieterportal
            </h1>
            <p className={themeClasses.textSecondary}>
              Ihr digitaler Service-Hub für alle Wohnungsangelegenheiten
            </p>
          </div>
        </div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          {/* Kontostand Alert */}
          <div className={`p-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800/50`}>
            <div className="flex items-center space-x-2">
              <span className="text-lg">💰</span>
              <span className={`text-sm font-medium text-red-800 dark:text-red-300`}>Kontostand</span>
            </div>
            <p className={`text-xl font-bold text-red-600 dark:text-red-400 mt-1`}>
              -40,00€ offen
            </p>
            <p className="text-xs text-red-700 dark:text-red-300 mt-2">
              Bitte mit der nächsten Miete ausgleichen.
            </p>
            <div className="mt-2 pt-2 border-t border-red-200 dark:border-red-800/50">
              <p className="text-xs text-red-600/80 dark:text-red-400/80">
                Letzte Zahlung: 780€ (unterzahlt)
              </p>
            </div>
          </div>

          <div className={`p-4 rounded-lg border ${themeClasses.border}`}>
            <div className="flex items-center space-x-2">
              <Home className={`w-5 h-5 ${themeClasses.textSecondary}`} />
              <span className={`text-sm ${themeClasses.textSecondary}`}>Wohnung</span>
            </div>
            <p className={`text-lg font-semibold ${themeClasses.text} mt-1`}>
              Musterstraße 123, Wohnung 4B
            </p>
          </div>
          
          <div className={`p-4 rounded-lg border ${themeClasses.border}`}>
            <div className="flex items-center space-x-2">
              <Users className={`w-5 h-5 ${themeClasses.textSecondary}`} />
              <span className={`text-sm ${themeClasses.textSecondary}`}>Verwaltung</span>
            </div>
            <p className={`text-lg font-semibold ${themeClasses.text} mt-1`}>
              Clara360 Immobilien
            </p>
          </div>
          
          <div className={`p-4 rounded-lg border ${themeClasses.border}`}>
            <div className="flex items-center space-x-2">
              <Clock className={`w-5 h-5 ${themeClasses.textSecondary}`} />
              <span className={`text-sm ${themeClasses.textSecondary}`}>Letzter Login</span>
            </div>
            <p className={`text-lg font-semibold ${themeClasses.text} mt-1`}>
              Heute, 14:30 Uhr
            </p>
          </div>
        </div>
      </div>

      {/* Service Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {dashboardCards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.id}
              onClick={card.action}
              className={`
                ${themeClasses.cardBackground} rounded-lg border ${themeClasses.border} 
                p-6 cursor-pointer transition-all duration-200 ${themeClasses.hover}
                hover:shadow-lg hover:scale-105
              `}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-lg ${card.bgColor}`}>
                  <Icon className={`w-6 h-6 ${card.color}`} />
                </div>
                <span className={`text-sm font-medium px-2 py-1 rounded-full ${card.bgColor} ${card.color}`}>
                  {card.count}
                </span>
              </div>
              
              <h3 className={`text-lg font-semibold ${themeClasses.text} mb-2`}>
                {card.title}
              </h3>
              
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {card.description}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderPlaceholder = (title, description) => (
    <div className={`${themeClasses.cardBackground} rounded-lg border ${themeClasses.border} p-8 text-center`}>
      <div className={`inline-flex p-4 rounded-full ${themeClasses.accent} mb-4`}>
        <Lightbulb className="w-8 h-8 text-white" />
      </div>
      <h2 className={`text-2xl font-bold ${themeClasses.text} mb-2`}>
        {title}
      </h2>
      <p className={`${themeClasses.textSecondary} mb-6`}>
        {description}
      </p>
      <div className={`inline-flex px-4 py-2 rounded-lg ${themeClasses.accent} text-white text-sm`}>
        Bald verfügbar
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'messages':
        return renderPlaceholder(
          'Nachrichten',
          'Direktkommunikation mit Ihrer Hausverwaltung. Stellen Sie Fragen, melden Sie Probleme oder erhalten Sie wichtige Updates.'
        );
      case 'documents':
        return renderPlaceholder(
          'Dokumente',
          'Zugriff auf alle wichtigen Dokumente wie Mietvertrag, Nebenkostenabrechnungen und Hausordnung.'
        );
      case 'announcements':
        return renderPlaceholder(
          'Digitales Schwarzes Brett',
          'Aktuelle Ankündigungen, Termine und wichtige Informationen für alle Mieter des Hauses.'
        );
      case 'maintenance':
        return renderPlaceholder(
          'Schadensmeldung',
          'Melden Sie Defekte oder Reparaturbedarf schnell und unkompliziert mit Foto-Upload und Status-Verfolgung.'
        );
      case 'consumption':
        return renderPlaceholder(
          'Verbrauchsanalyse',
          'Übersicht über Ihren Strom-, Wasser- und Heizverbrauch mit Vergleichsdaten und Spartipps.'
        );
      case 'appointments':
        return renderPlaceholder(
          'Termine',
          'Buchen Sie online Termine für Wohnungsbesichtigungen, Reparaturen oder Beratungsgespräche.'
        );
      default:
        return renderDashboard();
    }
  };

  return (
    <div className={`min-h-screen ${themeClasses.background} p-6`}>
      <div className="max-w-7xl mx-auto">
        {/* News Ticker */}
        <NewsTicker />
        {/* Navigation Tabs */}
        <div className={`${themeClasses.cardBackground} rounded-lg border ${themeClasses.border} p-2 mb-6`}>
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: Home },
              { id: 'messages', label: 'Nachrichten', icon: MessageSquare },
              { id: 'documents', label: 'Dokumente', icon: FileText },
              { id: 'announcements', label: 'Schwarzes Brett', icon: Bell },
              { id: 'maintenance', label: 'Schadensmeldung', icon: Settings },
              { id: 'consumption', label: 'Verbrauch', icon: BarChart3 },
              { id: 'appointments', label: 'Termine', icon: Calendar }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200
                    ${isActive 
                      ? `${themeClasses.accent} text-white shadow-md` 
                      : `${themeClasses.textSecondary} ${themeClasses.hover}`
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content Area */}
        {renderContent()}
      </div>
    </div>
  );
};

export default TenantPortal;
